import React from "react";
import { Printer } from "lucide-react";

/**
 * WalkaroundPrint — SINGLE-page A4 landscape capture sheet.
 *
 * Design constraint: the whole sheet MUST fit on ONE page so the manager can
 * print 5 copies for 5 staff and hand them out. Anything that pushes onto a
 * second page wastes paper and confuses the count.
 *
 * Layout budget (A4 landscape, 12mm margins → ~272×185mm usable):
 *   - Screen-only header w/ Print button: hidden on print
 *   - Print header (title + hint): ~14mm
 *   - Room/Walker/Date header strip: ~14mm
 *   - Main table 15 rows × ~9mm: ~150mm
 *   - Footer legend: ~5mm
 *   - Total: ~183mm — fits under 185mm with breathing room
 */
const HEADER_FIELDS = [
  { label: "Location / Room", width: "42%" },
  { label: "Walked by", width: "22%" },
  { label: "Date", width: "18%" },
  { label: "Page ___ of ___", width: "18%" },
];

const COLUMNS = [
  { label: "Category (Furniture / IT / Tool …)", width: "18%" },
  { label: "Name / description", width: "22%" },
  { label: "Qty", width: "5%" },
  { label: "Brand & Model", width: "17%" },
  { label: "Serial (opt)", width: "10%" },
  { label: "Cond.", width: "8%" },
  { label: "Notes / Assignee", width: "20%" },
];

const BLANK_ROWS = 15;

const WalkaroundPrint = () => (
  <div
    className="p-6 bg-white text-black"
    style={{ fontFamily: "Lexend, system-ui" }}
    data-testid="walkaround-print-page"
  >
    <style>{`
      @page { size: A4 landscape; margin: 10mm; }
      @media print {
        html, body { height: 100%; }
        .no-print { display: none !important; }
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .wa-sheet { page-break-inside: avoid; page-break-after: avoid; }
      }
      .wa-sheet { max-height: 185mm; }
      .wa-table { width: 100%; border-collapse: collapse; }
      .wa-table th, .wa-table td { border: 1px solid #000; padding: 5px 6px; vertical-align: top; }
      .wa-table th { background: #F4F8FB; font-size: 9.5px; text-transform: uppercase; letter-spacing: 0.4px; }
      .wa-table td { height: 22px; }
      .wa-hdr-cell { border: 1px solid #000; padding: 4px 8px; font-size: 10px; }
    `}</style>

    <div className="no-print mb-4 flex items-center justify-between">
      <div>
        <div className="text-xs uppercase tracking-wide text-[#607087]">
          Podlab · Assets
        </div>
        <div className="font-bold text-lg">Walk-around capture sheet</div>
        <div className="text-xs text-[#607087]">
          A4 landscape · one page · print multiple copies for other staff.
        </div>
      </div>
      <button
        onClick={() => window.print()}
        className="nb-btn nb-btn-primary px-4 py-2 flex items-center gap-2"
      >
        <Printer size={16} strokeWidth={2.5} /> Print
      </button>
    </div>

    <div className="wa-sheet">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[9px] uppercase tracking-wider text-[#607087]">
            Podlab / Footwork · Asset register
          </div>
          <div className="text-xl font-bold leading-tight">
            Walk-around capture sheet
          </div>
        </div>
        <div className="text-right text-[9px] text-[#607087] leading-snug">
          Fill in every desk, chair, machine, ladder, rack, tool.<br />
          Identical items (e.g. 8 boardroom chairs) — one row, Qty = 8.<br />
          Hand back to the manager when done.
        </div>
      </div>

      <table className="wa-table mb-2">
        <tbody>
          <tr>
            {HEADER_FIELDS.map((h) => (
              <td key={h.label} className="wa-hdr-cell" style={{ width: h.width }}>
                <div className="text-[8px] uppercase tracking-wider text-[#607087]">
                  {h.label}
                </div>
                <div style={{ height: 16 }} />
              </td>
            ))}
          </tr>
        </tbody>
      </table>

      <table className="wa-table">
        <thead>
          <tr>
            {COLUMNS.map((c) => (
              <th key={c.label} style={{ width: c.width }}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: BLANK_ROWS }).map((_, i) => (
            <tr key={i}>
              {COLUMNS.map((c, j) => (
                <td key={j}></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-2 text-[8.5px] text-[#607087] leading-snug">
        <b>Conditions:</b> N (new) · G (good) · F (fair) · W (worn) · B (broken) &nbsp;·&nbsp;
        <b>Categories:</b> Furniture — Office · Furniture — Meeting ·
        Racking &amp; Shelving · Storage &amp; Bins · IT — Laptops · IT — Peripherals ·
        IT — Networking · Tools — Hand · Tools — Power · Ladders &amp; Access ·
        Appliances · Safety &amp; PPE · Vehicles · Cameras &amp; AV · Signage ·
        Lighting · Cleaning Equipment · Ergonomics.
      </div>
    </div>
  </div>
);

export default WalkaroundPrint;
